import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAssetHierarchyTree } from "../fetchers/cmmsAssetHierarchy";
import { flattenAssetNodes } from "../features/cmms/assetHierarchyUtils";

/*
 * The CMMS asset node a machine is linked to, or null.
 *
 * The link lives on the node (`linkedMachineId`), not on the machine, so the only way to walk
 * it in this direction is to flatten the tree and search. That is cheap because the tree is one
 * cached request the whole machine page shares.
 *
 * Lifted out of MachineReportIssueButton when the machine page's work order tab needed the
 * same walk.
 * The query key is unchanged and deliberately identical to the one the work order page uses —
 * react-query then serves every caller on a machine page from one request, and no two callers
 * can disagree about which node a machine is.
 */
export function useAssetNodeForMachine(machineId, companyKey) {
  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: Boolean(companyKey), staleTime: 300000 },
  );

  const assetNode = useMemo(() => {
    const targetId = Number(machineId);
    return flattenAssetNodes(treeQuery.data).find((node) => node.linkedMachineId === targetId) ?? null;
  }, [treeQuery.data, machineId]);

  return {
    assetNode,
    // "Not linked" is only an answer once the tree has arrived. Before that the honest state is
    // "we do not know yet", and a caller that renders an empty state on it flashes
    // "this machine is not in the maintenance system" at every user on every load.
    isLoading: treeQuery.isLoading,
    isError: treeQuery.isError,
    error: treeQuery.error,
  };
}

export default useAssetNodeForMachine;
